import type { AdInfo } from '@softgames/ad-flow';
import { AdError, AdFlowErrorCodes, AdState, FallthroughAdProvider, AdType } from '@softgames/ad-flow';

import { Signal } from 'typed-signals';
import type { Pane } from 'tweakpane';
import type { PlatformAdModule } from '@hexa/app/platform/Platform';
import type { AdResponseValueType } from '@hexa/app/tracking/trackingModules/avo/Avo';
import type { AzerionBridge } from '@softgames/azerion-bridge';
import { isNullish } from '@softgames/type-guards';
import { soundManager } from '@hexa/app/SoundManager';
import { getLongPlayAdScheduler } from '../ads/longPlayAdScheduler';
import { resetAdSkipped, wasAdSkipped } from './gdAdEventObserver';

export class AzerionAds implements PlatformAdModule {
  public readonly onAdStarted = new Signal();

  public readonly onAdFinished = new Signal<(properties: { adInfo: AdInfo; adError: AdError | null }) => void>();

  private isAdCurrentlyShowing = false;

  constructor(private readonly bridge: AzerionBridge) {
    this.onAdStarted.connect(() => {
      this.isAdCurrentlyShowing = true;
      resetAdSkipped();
      // AZ-854: was soundManager.mute(). setAdActive also guards the gesture/visibility/
      // focus handlers so tapping to pause/resume a (pre-roll) ad can't unmute the game
      // audio over the ad.
      soundManager.setAdActive(true);
    });

    this.onAdFinished.connect(() => {
      this.isAdCurrentlyShowing = false;
      // AZ-854: was soundManager.unmute(). Clears the ad guard and restores the game audio.
      soundManager.setAdActive(false);
    });

    // AZ-998: long-play timing lives in the scheduler; it observes onAdStarted/onAdFinished.
    getLongPlayAdScheduler().attachAds(this);
  }

  public async showRewardedAd(slotName = ''): Promise<AdResponseValueType> {
    // AZ-998: rewarded has priority over a pending long-play request.
    getLongPlayAdScheduler().cancelPendingLongPlay();
    return this.showAd(AdType.Rewarded, slotName);
  }

  public async showInterstitialAd(slotName = ''): Promise<AdResponseValueType> {
    return this.showAd(AdType.Interstitial, slotName);
  }

  private async showAd(adType: AdType, slotName = ''): Promise<AdResponseValueType> {
    // AZ-998: never run two ads at once.
    if (this.isAdCurrentlyShowing) {
      return 'Closed';
    }

    this.onAdStarted.emit();

    const adInstance =
      adType === AdType.Rewarded ? await this.bridge.ads.getRewarded() : await this.bridge.ads.getInterstitial();

    let response: AdResponseValueType = 'Error';
    let adError: AdError | null = null;

    if (!isNullish(adInstance)) {
      await adInstance.load();
      const result = await adInstance.show();

      if (result.started) {
        // The Azerion bridge resolves a skipped rewarded ad as finished=true
        // (ALL_ADS_COMPLETED fires on skip), so never grant a reward when the
        // raw SKIPPED event was observed during the ad.
        const skippedReward = adType === AdType.Rewarded && wasAdSkipped();
        response = result.finished && !skippedReward ? 'Success' : 'Closed';
      } else {
        response = 'Error';
        adError = new AdError(1768988599, AdFlowErrorCodes.Unknown, 'Ad response did not start');
      }
    } else {
      adError = new AdError(1768988599, AdFlowErrorCodes.Unknown, 'Ad instance is nullish');
    }

    this.onAdFinished.emit({
      adInfo: {
        placementId: 'DEV_TEST',
        placementName: 'DEV_TEST',
        state: response === 'Success' ? AdState.Finished : AdState.Closed,
        slotName,
        network: FallthroughAdProvider.adNetwork,
        type: adType,
        id: 'dev_test',
        adShowDelayMs: 0,
        countRetries: 0,
      },
      adError,
    });

    return response;
  }

  public addDebugOptions(_pane: Pane): void {}

  public setupPrerollAd(): void {
    const waitForSoundManager = new Promise<void>((resolve) => {
      const checkInterval = setInterval(() => {
        if (soundManager.initiated) {
          clearInterval(checkInterval);
          resolve();
        }
      }, 500);
    });
    //eslint-disable-next-line @typescript-eslint/no-floating-promises
    waitForSoundManager.then(() => {
      //eslint-disable-next-line no-void
      void this.showInterstitialAd('preroll');
    });
  }

  public get isAdShowing(): boolean {
    return this.isAdCurrentlyShowing;
  }
}
